import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { hybridApi, type HybridFlow, type HybridKnowledge, type HybridPlan, type HybridTerm } from "@/lib/api";
import { Badge, Card, ErrorNote, Notice, Spinner, Table, Tabs, Td, Th, type Tone } from "@/components/ui";
import { PlanTree, SnippetDialog, outcomeLabel } from "@/features/hybrid";

// --- the robot's knowledge of mailboxes ---------------------------------------

// What every provider starts with, before its plan has learned anything: the
// typical courses of a mailbox call, the catalogue of eventualities, the words
// the robot reads (and what for), the keys as a box says them, the lines it
// never crosses, the built-in start plans, and the text the language model
// reads of all this. It lives in the code (internal/domain/hybrid/knowledge.go)
// and is shown here to be read, not edited.

const sections = [
  { id: "flows", label: "Abläufe" },
  { id: "situations", label: "Eventualitäten" },
  { id: "terms", label: "Stichwörter" },
  { id: "keys", label: "Tasten" },
  { id: "rules", label: "Schutzregeln" },
  { id: "seeds", label: "Startpläne" },
  { id: "model", label: "Was das Sprachmodell liest" },
];

export function KnowledgeTab() {
  const [params, setParams] = useSearchParams();
  const section = sections.some((s) => s.id === params.get("section")) ? params.get("section")! : "flows";
  const setSection = (id: string) => setParams({ tab: "knowledge", section: id }, { replace: true });
  const knowledge = useQuery({ queryKey: ["hybrid-knowledge"], queryFn: hybridApi.knowledge, staleTime: Infinity });

  if (knowledge.isLoading) return <Spinner />;
  if (knowledge.error || !knowledge.data) return <ErrorNote>Das Grundwissen konnte nicht geladen werden.</ErrorNote>;
  const k = knowledge.data;

  return (
    <div className="flex flex-col gap-4">
      <Notice>
        Das Grundwissen gilt für jeden Anbieter, bevor sein Plan etwas gelernt hat: wie ein Anruf bei einer Mobilbox
        typischerweise verläuft, welche Wörter der Roboter selbst liest und wofür, und die Linien, die er nie
        überschreitet. Welche Menüoption zum Ziel führt, entscheidet es nicht — das tun der Klang, der gelernte Wortlaut
        und das Sprachmodell; den Weg eines Anbieters lernt sein Plan. Es steht im Code
        (<span className="font-mono">internal/domain/hybrid/knowledge.go</span>) und ist hier zum Nachlesen.
      </Notice>
      <Tabs tabs={sections} active={section} onChange={setSection} />
      {section === "flows" && <Flows flows={k.flows} />}
      {section === "situations" && <Situations k={k} />}
      {section === "terms" && <Terms k={k} />}
      {section === "keys" && <Keys k={k} />}
      {section === "rules" && <Rules k={k} />}
      {section === "seeds" && <Seeds seeds={k.seeds} />}
      {section === "model" && <ModelText text={k.model_text} />}
    </div>
  );
}

// --- pieces ------------------------------------------------------------------

function CardHead({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <div className="border-b px-4 py-3" style={{ borderColor: "var(--border)" }}>
      <h2 className="text-base font-semibold">{title}</h2>
      {children && <div className="mt-0.5 text-sm muted">{children}</div>}
    </div>
  );
}

/** A word or phrase as the robot looks for it: wraps, unlike a badge. */
function Chip({ children, mono }: { children: ReactNode; mono?: boolean }) {
  return (
    <span
      className={
        "inline-block rounded-md bg-[var(--surface-sunken)] px-1.5 py-px text-xs ring-1 ring-inset ring-[var(--border)] " +
        (mono ? "font-mono" : "")
      }
    >
      {children}
    </span>
  );
}

function Chips({ words, mono }: { words?: string[]; mono?: boolean }) {
  if (!words?.length) return <span className="muted">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {words.map((w) => (
        <Chip key={w} mono={mono}>
          {w}
        </Chip>
      ))}
    </div>
  );
}

// --- the courses ---------------------------------------------------------------

function Flows({ flows }: { flows: HybridFlow[] }) {
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHead title="Typische Abläufe">
          Wie Mobilboxen auf dem Weg zur gespeicherten Begrüßung verlaufen, mit dem, woran der Roboter sie erkennt und
          was in ihm sich darum kümmert. Die Zeile „So liest es das Sprachmodell“ bekommt qwen bei jeder Frage mit —
          ohne Anbieter und ohne deren Tasten; „Gesehen bei“ ist nur für Sie.
        </CardHead>
        <ol className="grid gap-x-6 gap-y-1 px-4 py-3 text-sm sm:grid-cols-2">
          {flows.map((f, i) => (
            <li key={f.id}>
              <a href={`#flow-${f.id}`} className="hover:underline">
                <span className="mr-1 muted tabular-nums">{i + 1}.</span>
                {f.title}
              </a>
            </li>
          ))}
        </ol>
      </Card>
      {flows.map((f, i) => (
        <Card key={f.id}>
          <div id={`flow-${f.id}`} className="scroll-mt-4">
            <CardHead
              title={
                <>
                  <span className="mr-1.5 muted tabular-nums">{i + 1}.</span>
                  {f.title}
                </>
              }
            >
              {f.summary}
            </CardHead>
          </div>
          <Table dense>
            <thead>
              <tr>
                <Th>Die Box sagt oder tut</Th>
                <Th>Der Roboter</Th>
                <Th>Situation</Th>
              </tr>
            </thead>
            <tbody>
              {f.steps.map((s, j) => (
                <tr key={j}>
                  <Td className="text-xs">{s.box}</Td>
                  <Td className="text-xs font-medium">{s.robot}</Td>
                  <Td className="font-mono text-xs muted">{s.meaning ?? ""}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <dl className="grid gap-x-3 gap-y-1.5 px-4 py-3 text-xs sm:grid-cols-[10rem_1fr]">
            {f.signals?.length ? (
              <>
                <dt className="muted">Erkennungszeichen</dt>
                <dd>
                  <Chips words={f.signals} />
                </dd>
              </>
            ) : null}
            {f.seen && (
              <>
                <dt className="muted">Gesehen bei</dt>
                <dd>{f.seen}</dd>
              </>
            )}
            <dt className="muted">Im Roboter</dt>
            <dd>{f.handling}</dd>
            <dt className="muted">So liest es das Sprachmodell</dt>
            <dd className="italic">{f.model}</dd>
          </dl>
        </Card>
      ))}
    </div>
  );
}

// --- the catalogue -------------------------------------------------------------

const reactionLabels: Record<string, string> = {
  number: "Nummer eingeben",
  pin: "PIN eingeben",
  choose: "die Taste zum Ziel wählen (Sprachmodell, bis der Plan die Wahl kennt)",
  wait_beep: "Signalton abwarten, aufsprechen",
  done: "Erfolg — das Sprachmodell bestätigt beim ersten Mal",
  abort: "Anruf beenden — das Sprachmodell bestätigt beim ersten Mal",
  retry: "Aufnahme wiederholen",
  listen: "weiter zuhören",
  llm: "das Sprachmodell entscheidet",
};

const phaseLabels: [number, string][] = [
  [1, "vor der PIN"],
  [2, "angemeldet"],
  [4, "nach dem Aufsprechen"],
];

// The half of the playbook every provider shares: what systemone may read a
// prompt as, what the robot does when the plan has no step of its own, and the
// wording a box typically uses for it.
function Situations({ k }: { k: HybridKnowledge }) {
  const wording = (meaning: string) =>
    k.terms
      .filter((t) => t.use.includes("model") && (t.meaning === meaning || (meaning === "menu" && t.meaning === "main_menu")))
      .flatMap((t) => t.de);
  return (
    <Card>
      <CardHead title="Eventualitäten">
        Der gemeinsame Teil jedes Playbooks. Die Situationen, als die eine Ansage gelten kann, die der Plan noch nicht kennt, als
        eine dieser Situationen — je Phase des Anrufs nur die möglichen, mit dem kurzen Etikett links. Was ein Anruf so
        findet, kommt mit Aufnahme als Schnipsel in den Plan des Anbieters. Die typischen Wortlaute liest nur das
        Sprachmodell.
      </CardHead>
      <Table dense>
        <thead>
          <tr>
            <Th>Situation</Th>
            <Th>Situation</Th>
            <Th>Reaktion</Th>
            <Th>Phase</Th>
            <Th>Typische Wortlaute</Th>
          </tr>
        </thead>
        <tbody>
          {k.situations.map((s) => (
            <tr key={s.meaning}>
              <Td className="font-mono text-xs">{s.meaning}</Td>
              <Td className="text-xs">„{s.criterion}“</Td>
              <Td className="text-xs">
                {reactionLabels[s.reaction] ?? s.reaction}
                {s.outcome && <span className="muted"> ({outcomeLabel(s.outcome)})</span>}
              </Td>
              <Td className="text-xs muted">
                {phaseLabels
                  .filter(([bit]) => (s.phases & bit) !== 0)
                  .map(([, l]) => l)
                  .join(", ")}
              </Td>
              <Td className="text-xs">
                <Chips words={wording(s.meaning)} />
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

// --- the lexicon -----------------------------------------------------------------

const useLabels: Record<string, { label: string; tone: Tone }> = {
  parser: { label: "Tasten lesen", tone: "info" },
  veto: { label: "Verbotsliste", tone: "danger" },
  back: { label: "Zurück-Taste", tone: "neutral" },
  end_key: { label: "End-Taste", tone: "warning" },
  model: { label: "Sprachmodell", tone: "success" },
};

const matchLabels: Record<string, string> = {
  substring: "auch mitten im Wort",
  word_start: "am Wortanfang",
  phrase: "als eigene Wörter",
};

function TermTable({ terms, vetoPatterns }: { terms: HybridTerm[]; vetoPatterns?: string[] }) {
  return (
    <Table dense>
      <thead>
        <tr>
          <Th>Begriff</Th>
          <Th>Deutsch</Th>
          <Th>Englisch</Th>
          <Th>Gelesen von</Th>
        </tr>
      </thead>
      <tbody>
        {terms.map((t) => (
          <tr key={t.id}>
            <Td className="text-xs">
              <div className="font-medium">{t.label}</div>
              <div className="font-mono muted">
                {t.id}
                {t.meaning && t.meaning !== t.id ? ` · ${t.meaning}` : ""}
              </div>
              <div className="muted">{matchLabels[t.match] ?? t.match}</div>
              {t.note && <div className="mt-1 muted">{t.note}</div>}
              {t.id === "veto" && vetoPatterns?.length ? (
                <div className="mt-1">
                  <div className="muted">Dazu die Muster:</div>
                  <Chips words={vetoPatterns} mono />
                </div>
              ) : null}
            </Td>
            <Td className="text-xs">
              <Chips words={t.de} />
            </Td>
            <Td className="text-xs">
              <Chips words={t.en} />
            </Td>
            <Td className="text-xs">
              <div className="flex flex-wrap gap-1">
                {t.use.map((u) => (
                  <Badge key={u} tone={useLabels[u]?.tone ?? "neutral"}>
                    {useLabels[u]?.label ?? u}
                  </Badge>
                ))}
              </div>
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function Terms({ k }: { k: HybridKnowledge }) {
  const robot = k.terms.filter((t) => t.use.some((u) => u !== "model"));
  const model = k.terms.filter((t) => t.use.every((u) => u === "model"));
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHead title="Wörter, die der Roboter selbst liest">
          Nur an diesen Stellen vergleicht der Roboter Wörter: welche Tasten eine Ansage nennt und wofür, das letzte
          Netz gegen verbotene Optionen, die Taste zurück, und wie die Aufnahme endet. Die Wahl der Option zum Ziel ist
          nie darunter.
        </CardHead>
        <TermTable terms={robot} vetoPatterns={k.veto_patterns} />
      </Card>
      <Card>
        <CardHead title="Typische Wortlaute für das Sprachmodell">
          Wie Mobilboxen die Situationen typischerweise sagen. qwen liest sie als Erfahrung; was die Box gerade sagt,
          geht vor.
        </CardHead>
        <TermTable terms={model} />
      </Card>
    </div>
  );
}

// --- keys ---------------------------------------------------------------------

function Keys({ k }: { k: HybridKnowledge }) {
  const byKey = new Map<string, typeof k.key_words>();
  for (const kw of k.key_words) byKey.set(kw.key, [...(byKey.get(kw.key) ?? []), kw]);
  const order = [..."0123456789*#"].filter((key) => byKey.has(key));
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHead title="Tasten, wie eine Box sie sagt">
          „Drücken Sie die Drei“, „mit der Sterntaste“, „press the hash key“ — und wie Whisper eine gesprochene Ziffer
          manchmal schreibt („die Einer“). Das Tasten-Lesen erkennt beide Reihenfolgen: „Für Ansagen drücken Sie die 3“
          und „Drücken Sie die 3 für Ansagen“, auch ohne Satzzeichen.
        </CardHead>
        <Table dense>
          <thead>
            <tr>
              <Th>Taste</Th>
              <Th>Gesprochen</Th>
              <Th>Von Whisper verhört</Th>
            </tr>
          </thead>
          <tbody>
            {order.map((key) => {
              const words = byKey.get(key) ?? [];
              const said = words.filter((w) => !w.misheard);
              const misheard = words.filter((w) => w.misheard);
              return (
                <tr key={key}>
                  <Td className="font-mono text-sm font-medium">{key}</Td>
                  <Td className="text-xs">
                    <Chips words={said.map((w) => (w.lang === "en" ? `${w.word} (en)` : w.word))} />
                  </Td>
                  <Td className="text-xs">
                    <Chips words={misheard.map((w) => (w.after ? `${w.after} ${w.word}` : w.word))} />
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card>
      <Card>
        <CardHead title="Wenn die Box nach dem Ende der Aufnahme schweigt">
          Dann nimmt sie wohl noch auf: die End-Taste war nicht ihre. Nach 5 s Stille (ohne gedrückte Taste 8 s)
          versucht der Roboter der Reihe nach diese Tasten, höchstens zwei weitere; der Plan lernt die, auf die die Box
          reagiert hat.
        </CardHead>
        <div className="px-4 py-3">
          <Chips words={k.end_key_fallbacks} mono />
        </div>
      </Card>
    </div>
  );
}

// --- rules ----------------------------------------------------------------------

function Rules({ k }: { k: HybridKnowledge }) {
  return (
    <Card>
      <CardHead title="Schutzregeln">
        Die Linien, die der Roboter nicht überschreitet, wie auch immer das Sprachmodell argumentiert. Sie sehen auf das,
        was der Anruf getan hat, nicht auf die Worte der Box — bis auf die Verbotsliste.
      </CardHead>
      <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
        {k.rules.map((r) => (
          <li key={r.id} className="px-4 py-3 text-sm">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-medium">{r.title}</span>
              <span className="font-mono text-xs muted">{r.where}</span>
            </div>
            <p className="mt-0.5 text-sm muted">{r.text}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// --- start plans ----------------------------------------------------------------

function Seeds({ seeds }: { seeds: { provider: string; plan: HybridPlan }[] }) {
  const [snippet, setSnippet] = useState<{ plan: HybridPlan; id: string } | null>(null);
  return (
    <div className="flex flex-col gap-4">
      <Notice>
        Ein echter Anbieter beginnt ohne Plan: sein erster Anruf erkundet die Box, qwen entscheidet jeden Schritt, und
        der erfolgreiche Anruf wird Version 1. Einen eingebauten Startplan hat nur die simulierte Box, für Tests und
        die Entwicklung.
      </Notice>
      {seeds.map((s) => (
        <Card key={s.provider}>
          <CardHead title={`Startplan ${s.provider}`}>
            Der Ablauf in der Reihenfolge, in der ein Anruf die Zustände trifft; je Zustand die Zweige „wenn / sonst
            wenn“ mit Schnipsel, Tasten und Ziel.
          </CardHead>
          <PlanTree plan={s.plan} onSnippet={(id) => setSnippet({ plan: s.plan, id })} />
        </Card>
      ))}
      {snippet && <SnippetDialog plan={snippet.plan} id={snippet.id} onClose={() => setSnippet(null)} />}
    </div>
  );
}

// --- the model's text -----------------------------------------------------------

function ModelText({ text }: { text: string }) {
  return (
    <Card>
      <CardHead title="Was das Sprachmodell liest">
        Dieser Text steht bei jeder Frage an qwen nach seinen Regeln (Ziel, Antwortformat, Tabus). Er ist aus den
        Abläufen und den typischen Wortlauten erzeugt.
      </CardHead>
      <pre className="overflow-x-auto px-4 py-3 text-xs leading-relaxed whitespace-pre-wrap">{text}</pre>
    </Card>
  );
}
